import { Button, ComboboxParsedItem, Group, MultiSelect, OptionsFilter, Radio, Stack, Text } from '@mantine/core'
import { useCallback } from 'react'
import type { ProjectTagRow } from '@/types/api'
import type { EmergencyTagFilterMode } from '@/components/emergency/EmergencyCurrentSettings'
export type { EmergencyTagFilterMode } from '@/components/emergency/EmergencyCurrentSettings'
export {
  tagFilterModeFromEmergency,
  tagFilterPayload,
} from '@/components/emergency/EmergencyCurrentSettings'

type EmergencyTagFilterPanelProps = {
  mode: EmergencyTagFilterMode
  onModeChange: (mode: EmergencyTagFilterMode) => void
  selectedTagIds: string[]
  onSelectedTagIdsChange: (ids: string[]) => void
  tags: ProjectTagRow[]
  loading?: boolean
  noProject?: boolean
  disabled?: boolean
}

export function EmergencyTagFilterPanel({
  mode,
  onModeChange,
  selectedTagIds,
  onSelectedTagIdsChange,
  tags,
  loading = false,
  noProject = false,
  disabled = false,
}: EmergencyTagFilterPanelProps) {
  const activeTags = tags.filter((tag) => tag.is_active !== false)
  const activeTagById = new Map(activeTags.map((t) => [t.id, t]))
  const selectedActiveIds = selectedTagIds.filter((id) => activeTagById.has(id))

  const onModeSwitch = (next: EmergencyTagFilterMode) => {
    if (next !== mode) {
      onSelectedTagIdsChange([])
    }
    onModeChange(next)
  }

  const onTagChange = (ids: string[]) => {
    onSelectedTagIdsChange(ids)
  }

  const toSearchString = (input: unknown): string => {
    if (typeof input === 'string') return input
    if (input == null) return ''
    return String(input)
  }

  const tagOptions = activeTags.map((tag) => ({
    value: tag.id,
    label: tag.name || tag.id,
  }))
  const canPickAll = tagOptions.length > 0 && selectedActiveIds.length < tagOptions.length
  const canReset = selectedActiveIds.length > 0

  const filter = useCallback<OptionsFilter>(({ options, search, limit }) => {
    const q = toSearchString(search).trim().toLowerCase()
    if (!q) return options.slice(0, limit)
    const filtered: ComboboxParsedItem[] = []
    for (const item of options) {
      if (filtered.length >= limit) break
      if (!('value' in item) || 'group' in item) continue
      const label = toSearchString(item.label).toLowerCase()
      const value = toSearchString(item.value).toLowerCase()
      if (label.includes(q) || value.includes(q)) {
        filtered.push(item)
      }
    }
    return filtered
  }, [])

  const showTagPicker = mode === 'exclude' || mode === 'include'

  return (
    <Stack gap="sm" style={disabled ? { opacity: 0.55, pointerEvents: 'none' } : undefined}>
      <Text size="sm" fw={500}>
        Фильтр по меткам
      </Text>
      <Radio.Group value={mode} onChange={(v) => onModeSwitch(v as EmergencyTagFilterMode)}>
        <Stack gap="xs">
          <Radio value="all" label="Без фильтра по меткам" />
          <Radio
            value="exclude"
            label="Исключить метки"
            description="Обращения с выбранными метками не попадут в аварию"
          />
          <Radio
            value="include"
            label="Только с метками"
            description="В аварию попадут только обращения с хотя бы одной выбранной меткой"
          />
        </Stack>
      </Radio.Group>

      {!showTagPicker ? null : noProject ? (
        <Text size="sm" c="dimmed">
          Выберите проект
        </Text>
      ) : loading ? (
        <Text size="sm" c="dimmed">
          Загрузка меток…
        </Text>
      ) : activeTags.length === 0 ? (
        <Text size="sm" c="dimmed">
          Нет активных меток, используемых в правилах
        </Text>
      ) : (
        <Stack gap={6}>
          <Group gap={6}>
            <Button
              size="compact-xs"
              variant="light"
              disabled={!canPickAll}
              onClick={() => onSelectedTagIdsChange(tagOptions.map((t) => t.value))}
            >
              Отметить всё
            </Button>
            <Button
              size="compact-xs"
              variant="default"
              disabled={!canReset}
              onClick={() => onSelectedTagIdsChange([])}
            >
              Сбросить
            </Button>
            <Text size="xs" c="dimmed">
              {selectedActiveIds.length}/{tagOptions.length}
            </Text>
          </Group>
          <MultiSelect
            data={tagOptions}
            value={selectedActiveIds}
            onChange={onTagChange}
            placeholder="Начните вводить название метки"
            searchable
            clearable
            limit={40}
            maxDropdownHeight={178}
            comboboxProps={{ withinPortal: true, shadow: 'md' }}
            filter={filter}
          />
        </Stack>
      )}
    </Stack>
  )
}

/** @deprecated alias */
export const EmergencyScopePanel = EmergencyTagFilterPanel
